import type { AssetNamespace, ChainNamespace, ChainReference } from "./types";
import { CAIPValidationError } from "./errors";

export const SOLANA_CHAIN_ID = 1399811149;
export const SOLANA_CHAIN_REFERENCE = "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp"; // Solana mainnet genesis hash
export const SUI_CHAIN_REFERENCE = "mainnet";
export const EVM_NATIVE_SLIP44_COIN_TYPE = "60";
export const EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS =
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
export const SOLANA_NATIVE_SLIP44_COIN_TYPE = "501";
export const SOLANA_NATIVE_TOKEN_ADDRESS =
  "So11111111111111111111111111111111111111112";

/**
 * Chain-agnostic CAIP-10 reference for EVM EOAs.
 *
 * Per [CAIP-10 §Abstract Account Addresses](https://github.com/ChainAgnostic/CAIPs/blob/main/CAIPs/caip-10.md#abstract-account-addresses),
 * an Ethereum account that is not bound to a specific EIP-155 chain
 * should use the special chain reference `"0"`. The same secp256k1 EOA
 * works on every EIP-155 chain, so binding it to one is misleading.
 *
 * Used by `Builder.buildAccountId` when the caller omits `reference`
 * for an `eip155` namespace.
 */
export const EIP155_CHAIN_AGNOSTIC_REFERENCE: ChainReference = "0";

/**
 * Per-namespace default CAIP-10 reference lookup.
 *
 * Used by `Builder.buildAccountId` when `reference` is omitted. EIP-155 uses
 * its chain-agnostic `"0"` reference; Sui uses its canonical `"mainnet"`
 * reference. Namespaces without a default (Solana — every account is bound to
 * a specific cluster) are absent, and callers must pass an explicit reference.
 *
 * Add a new entry here when a namespace gains a default reference convention;
 * call sites do not need to change.
 */
export const CHAIN_AGNOSTIC_REFERENCE: Partial<
  Record<ChainNamespace, ChainReference>
> = {
  eip155: EIP155_CHAIN_AGNOSTIC_REFERENCE,
  sui: SUI_CHAIN_REFERENCE,
};

type NamespaceConfig = {
  chainNamespace: ChainNamespace;
  assetNamespace: AssetNamespace;
};

const NAMESPACES_EVM: NamespaceConfig = {
  chainNamespace: "eip155",
  assetNamespace: "erc20",
};

const NAMESPACES_SOLANA: NamespaceConfig = {
  chainNamespace: "solana",
  assetNamespace: "spl",
};

const NAMESPACES_SUI: NamespaceConfig = {
  chainNamespace: "sui",
  assetNamespace: "coin",
};

export const NAMESPACES = (chainNamespace: ChainNamespace): NamespaceConfig => {
  switch (chainNamespace) {
    case "eip155":
      return NAMESPACES_EVM;
    case "solana":
      return NAMESPACES_SOLANA;
    case "sui":
      return NAMESPACES_SUI;
    default:
      throw new Error(`Unsupported chain namespace: ${chainNamespace}`);
  }
};

/**
 * Detect chain namespace from chainId
 * @param chainId - Numeric chain ID as string
 * @returns ChainNamespace ("eip155" or "solana")
 * @throws {CAIPValidationError} if the chain ID is neither Solana nor numeric
 */
export const detectChainNamespace = (chainId: string): ChainNamespace => {
  if (chainId === SOLANA_CHAIN_ID.toString()) {
    return "solana";
  }
  if (/^\d+$/u.test(chainId)) {
    return "eip155";
  }
  throw new CAIPValidationError(
    `Unsupported chain ID: ${chainId}. Expected a numeric EIP-155 chain ID or ${SOLANA_CHAIN_ID}.`,
    chainId,
  );
};
